/**
 * Background Geolocation Service
 * Provides background location tracking on native platforms with web fallback
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
import {BackgroundGeolocationPlugin} from "@capacitor-community/background-geolocation";
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>("BackgroundGeolocation");
import { CapacitorService, GeolocationPosition } from './capacitor';
import { LoggingService } from './logging';

export interface BackgroundGeolocationConfig {
  backgroundMessage?: string;
  backgroundTitle?: string;
  requestPermissions?: boolean;
  stale?: boolean;
  distanceFilter?: number;
}

export interface BackgroundGeolocationWatcher {
  id: string;
  remove: () => Promise<void>;
}

/**
 * Background Geolocation Service
 * Uses background tracking on native platforms, falls back to foreground tracking on web
 */
export const BackgroundGeolocationService = {
  /**
   * Check if background geolocation is available
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  },

  /**
   * Initialize background geolocation with configuration
   */
  async initialize(config: BackgroundGeolocationConfig = {}): Promise<void> {
    if (!this.isAvailable()) {
      // Web platform - no initialization needed, will use standard geolocation
      return;
    }

    try {
      // Configure the background geolocation plugin for native platforms
      // Note: The plugin will handle notification channel creation automatically
      LoggingService.Log('Background Geolocation initialized for native platform');
    } catch (error) {
      console.error('Error initializing background geolocation:', error);
      throw error;
    }
  },

  /**
   * Watch position changes with background support
   * On native: Uses background geolocation
   * On web: Falls back to standard geolocation
   */
  async watchPosition(
    callback: (position: GeolocationPosition | null, error?: any) => void,
    config: BackgroundGeolocationConfig = {}
  ): Promise<BackgroundGeolocationWatcher> {
    if (!this.isAvailable()) {
      // Web platform - probe first to determine what accuracy level works,
      // then watch with that accuracy. This handles devices without GPS
      // (like Amazon Fire tablets) where high accuracy times out.
      LoggingService.Log('[BackgroundGeolocation] Web platform - probing geolocation capabilities...');

      let useHighAccuracy = true;
      try {
        const probeResult = await CapacitorService.geolocation.getCurrentPositionWithInfo({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
        useHighAccuracy = probeResult.usedHighAccuracy;
        LoggingService.Log(`[BackgroundGeolocation] Probe succeeded (highAccuracy: ${useHighAccuracy})`);

        // Immediately call the callback with the probe result so we don't wait for watch
        callback(probeResult.position);
      } catch (probeError: any) {
        LoggingService.Log(`[BackgroundGeolocation] Probe failed: ${probeError.message}`);
        // If probe fails entirely, let watchPosition try and report the error
        useHighAccuracy = false;
      }

      const watchId = await CapacitorService.geolocation.watchPosition(
        callback,
        {
          enableHighAccuracy: useHighAccuracy,
          timeout: 30000, // 30 seconds
          maximumAge: 5000, // 5 seconds
        }
      );

      return {
        id: watchId,
        remove: async () => {
          await CapacitorService.geolocation.clearWatch(watchId);
        },
      };
    }

    // Native platform - use background geolocation
    try {
      const watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage:
            config.backgroundMessage ||
            'PIT Count is sharing your route to help coordinate coverage.',
          backgroundTitle:
            config.backgroundTitle || 'Route Sharing Active',
          requestPermissions: config.requestPermissions ?? true,
          stale: config.stale ?? false,
          distanceFilter: config.distanceFilter ?? 30, // 30 meters (~100 feet)
        },
        (location, error) => {
          if (error) {
            callback(null, error);
            return;
          }

          if (location) {
            // Convert background geolocation format to our standard format
            callback({
              coords: {
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
                altitude: location.altitude ?? null,
                altitudeAccuracy: location.altitudeAccuracy ?? null,
                heading: location.bearing ?? null,
                speed: location.speed ?? null,
              },
              timestamp: location.time ?? Date.now(), // Default to current time if time is null
            });
          }
        }
      );

      return {
        id: watcherId,
        remove: async () => {
          await BackgroundGeolocation.removeWatcher({ id: watcherId });
        },
      };
    } catch (error) {
      console.error('Error adding background geolocation watcher:', error);
      throw error;
    }
  },

  /**
   * Remove a watcher
   */
  async removeWatcher(watcherId: string): Promise<void> {
    if (!this.isAvailable()) {
      // Web platform
      await CapacitorService.geolocation.clearWatch(watcherId);
      return;
    }

    // Native platform
    await BackgroundGeolocation.removeWatcher({ id: watcherId });
  },

  /**
   * Open native app settings
   * Useful for directing users to enable background location permissions
   */
  async openSettings(): Promise<boolean> {
    if (!this.isAvailable()) {
      console.warn('openSettings is only available on native platforms');
      return false;
    }

    try {
      await BackgroundGeolocation.openSettings();
      return true;
    } catch (error) {
      console.error('Error opening settings:', error);
      return false;
    }
  },

  /**
   * Request location permissions
   * On native platforms, this will request the appropriate permissions
   * On web, delegates to standard geolocation permissions
   */
  async requestPermissions(): Promise<boolean> {
    return await CapacitorService.geolocation.requestPermissions();
  },

  /**
   * Check location permissions
   */
  async checkPermissions(): Promise<string> {
    return await CapacitorService.geolocation.checkPermissions();
  },
};

export default BackgroundGeolocationService;
